// Constantes pour les sélecteurs DOM fréquemment utilisés
const SELECTORS = {
  CART_ITEMS: '#main-cart-items, #CartDrawer-CartItems',
  CART_ERRORS: '#cart-errors, #CartDrawer-CartErrors',
  CART_DRAWER: 'cart-drawer',
  CARE_SHEET_ITEM: '.cart-item--care-sheet',
  LOADING_SPINNER: '.loading__spinner',
  CART_ITEM_NAME: '.cart-item__name',
};

// Fonctions utilitaires
function getSectionInnerHTMLSafe(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
}

function toggleCartEmptyState(cartItems, isEmpty) {
  // Mettre à jour l'état du panier (vide/non-vide)
  if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);

  const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
  const cartFooter = document.getElementById('main-cart-footer');

  if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
  if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
}

function handleCartErrors(error, customMessage) {
  console.error('Erreur panier:', error);
  const errors = document.querySelector(SELECTORS.CART_ERRORS);
  if (errors) {
    errors.textContent = customMessage || (window.cartStrings ? window.cartStrings.error : 'Une erreur est survenue');
  }
}

function safePublishEvent(eventName, data) {
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS[eventName], data);
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      // Vérifier si c'est un groupe de feuille de soins avec plusieurs articles à supprimer
      if (this.dataset.groupType === 'care-sheet' && event.currentTarget.querySelector('button').dataset.itemKeys) {
        const itemKeys = event.currentTarget.querySelector('button').dataset.itemKeys.split(',');
        // Supprimer tous les articles du groupe en utilisant leurs clés uniques
        this.removeAllCareSheetItems(cartItems, itemKeys);
      } else if (this.dataset.groupType === 'bundle' && event.currentTarget.querySelector('button').dataset.itemKeys) {
        // Vérifier si c'est un bundle avec plusieurs articles à supprimer
        const itemKeys = event.currentTarget
          .querySelector('button')
          .dataset.itemKeys.split(',')
          .filter((key) => key.trim());
        // Supprimer tous les articles du bundle en utilisant leurs clés uniques
        this.removeAllBundleItems(cartItems, itemKeys);
      } else {
        // Comportement normal pour les autres articles
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }

  /**
   * Supprime tous les articles d'une feuille de soins en utilisant leurs clés uniques
   * @param {HTMLElement} cartItems - L'élément du panier contenant les articles
   * @param {Array} itemKeys - Tableau de clés d'articles à supprimer
   */
  removeAllCareSheetItems(cartItems, itemKeys) {
    // Identifier l'élément de feuille de soins pour le loading
    const careSheetItem = this.closest(SELECTORS.CARE_SHEET_ITEM);

    // Activer le spinner de chargement
    this.enableCareSheetLoading(careSheetItem);

    // Préparer l'objet updates avec les clés valides
    const updates = this._prepareItemUpdates(itemKeys);

    // Préparer la requête
    const sections = this._getSectionsForRequest(cartItems);
    const body = JSON.stringify({ updates, sections });

    // Envoyer la requête
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this._handleCareSheetRemovalSuccess(cartItems, state))
      .catch((error) => handleCartErrors(error))
      .finally(() => this.disableCareSheetLoading(careSheetItem));
  }

  /**
   * Supprime tous les articles d'un bundle en utilisant leurs clés uniques
   * @param {HTMLElement} cartItems - L'élément du panier contenant les articles
   * @param {Array} itemKeys - Tableau de clés d'articles à supprimer
   */
  removeAllBundleItems(cartItems, itemKeys) {
    // Identifier l'élément de bundle pour le loading
    const bundleItem = this.closest('.cart-item--bundle');

    // Activer le spinner de chargement
    this.enableBundleLoading(bundleItem);

    // Préparer l'objet updates avec les clés valides
    const updates = this._prepareItemUpdates(itemKeys);

    // Préparer la requête
    const sections = this._getSectionsForRequest(cartItems);
    const body = JSON.stringify({ updates, sections });

    // Envoyer la requête
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this._handleBundleRemovalSuccess(cartItems, state))
      .catch((error) => handleCartErrors(error))
      .finally(() => this.disableBundleLoading(bundleItem));
  }

  /**
   * Prépare un objet updates avec les clés d'articles valides
   * @private
   */
  _prepareItemUpdates(itemKeys) {
    const updates = {};
    itemKeys.forEach((key) => {
      if (key && key.trim()) {
        updates[key] = 0; // Mettre la quantité à zéro
      }
    });
    return updates;
  }

  /**
   * Récupère les sections à mettre à jour dans la requête
   * @private
   */
  _getSectionsForRequest(cartItems) {
    return cartItems.getSectionsToRender ? cartItems.getSectionsToRender().map((section) => section.section) : [];
  }

  /**
   * Gère la réponse réussie de la suppression de la feuille de soins
   * @private
   */
  _handleCareSheetRemovalSuccess(cartItems, state) {
    const parsedState = JSON.parse(state);

    // Vérifier s'il y a des erreurs
    if (parsedState.errors) {
      handleCartErrors(null, parsedState.errors);
      return;
    }

    // Mettre à jour l'interface
    toggleCartEmptyState(cartItems, parsedState.item_count === 0);

    // Mettre à jour les sections DOM
    this._updateDOMSections(cartItems, parsedState);

    // Gérer le focus
    this._handleFocusAfterRemoval(parsedState.item_count === 0);

    // Publier l'événement de mise à jour
    safePublishEvent('cartUpdate', {
      source: 'cart-items',
      cartData: parsedState,
    });
  }

  /**
   * Met à jour les sections DOM avec les nouvelles données
   * @private
   */
  _updateDOMSections(cartItems, parsedState) {
    if (!cartItems.getSectionsToRender) return;

    cartItems.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      // Obtenir le contenu HTML
      const htmlContent =
        typeof cartItems.getSectionInnerHTML === 'function'
          ? cartItems.getSectionInnerHTML(parsedState.sections[section.section], section.selector)
          : getSectionInnerHTMLSafe(parsedState.sections[section.section], section.selector);

      elementToReplace.innerHTML = htmlContent;
    });
  }

  /**
   * Gère le focus après la suppression des articles
   * @private
   */
  _handleFocusAfterRemoval(isEmpty) {
    if (typeof trapFocus !== 'function') return;

    const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
    if (!cartDrawerWrapper) return;

    if (isEmpty) {
      const emptyDrawer = cartDrawerWrapper.querySelector('.drawer__inner-empty');
      const firstLink = cartDrawerWrapper.querySelector('a');
      if (emptyDrawer && firstLink) {
        trapFocus(emptyDrawer, firstLink);
      }
    } else if (document.querySelector('.cart-item')) {
      trapFocus(cartDrawerWrapper, document.querySelector(SELECTORS.CART_ITEM_NAME));
    }
  }

  /**
   * Active le chargement sur la feuille de soins
   */
  enableCareSheetLoading(careSheetItem) {
    if (!careSheetItem) return;

    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Afficher le spinner sur l'élément de la feuille de soins
    const spinner = careSheetItem.querySelector(SELECTORS.LOADING_SPINNER);
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    // Masquer les erreurs précédentes
    const errors = document.querySelector(SELECTORS.CART_ERRORS);
    if (errors) {
      errors.textContent = '';
    }

    document.activeElement.blur();
  }

  /**
   * Désactive le chargement sur la feuille de soins
   */
  disableCareSheetLoading(careSheetItem) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      setTimeout(() => {
        mainCartItems.classList.remove('cart__items--disabled');
      }, 500);
    }

    // Masquer le spinner si présent
    if (careSheetItem) {
      const spinner = careSheetItem.querySelector(SELECTORS.LOADING_SPINNER);
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  /**
   * Gère la réponse réussie de la suppression du bundle
   * @private
   */
  _handleBundleRemovalSuccess(cartItems, state) {
    const parsedState = JSON.parse(state);

    // Vérifier s'il y a des erreurs
    if (parsedState.errors) {
      handleCartErrors(null, parsedState.errors);
      return;
    }

    // Mettre à jour l'interface
    toggleCartEmptyState(cartItems, parsedState.item_count === 0);

    // Mettre à jour les sections DOM
    this._updateDOMSections(cartItems, parsedState);

    // Gérer le focus
    this._handleFocusAfterRemoval(parsedState.item_count === 0);

    // Publier l'événement de mise à jour
    safePublishEvent('cartUpdate', {
      source: 'cart-items',
      cartData: parsedState,
    });
  }

  /**
   * Active le chargement sur le bundle
   */
  enableBundleLoading(bundleItem) {
    if (!bundleItem) return;

    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Afficher le spinner sur l'élément du bundle
    const spinner = bundleItem.querySelector(SELECTORS.LOADING_SPINNER);
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    // Masquer les erreurs précédentes
    const errors = document.querySelector(SELECTORS.CART_ERRORS);
    if (errors) {
      errors.textContent = '';
    }

    document.activeElement.blur();
  }

  /**
   * Désactive le chargement sur le bundle
   */
  disableBundleLoading(bundleItem) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      setTimeout(() => {
        mainCartItems.classList.remove('cart__items--disabled');
      }, 500);
    }

    // Masquer le spinner si présent
    if (bundleItem) {
      const spinner = bundleItem.querySelector(SELECTORS.LOADING_SPINNER);
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  /**
   * Valide la quantité entrée par l'utilisateur
   * @param {Event} event - L'événement de changement
   */
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const min = event.target.dataset.min;
    const max = parseInt(event.target.max);
    const step = parseInt(event.target.step);

    // Vérifier les différentes contraintes de quantité
    const message = this._getQuantityValidationMessage(inputValue, min, max, step);

    if (message) {
      // En cas d'erreur, afficher le message et mettre à jour la validité
      this.setValidity(event, index, message);
    } else {
      // Si valide, mettre à jour la quantité
      event.target.setCustomValidity('');
      event.target.reportValidity();

      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  /**
   * Obtient le message de validation de quantité approprié
   * @private
   */
  _getQuantityValidationMessage(value, min, max, step) {
    if (value < min) {
      return window.quickOrderListStrings.min_error.replace('[min]', min);
    }

    if (max && value > max) {
      return window.quickOrderListStrings.max_error.replace('[max]', max);
    }

    if (step && value % step !== 0) {
      return window.quickOrderListStrings.step_error.replace('[step]', step);
    }

    return '';
  }

  /**
   * Gestionnaire d'événements pour le changement de quantité
   */
  onChange(event) {
    this.validateQuantity(event);
  }

  /**
   * Met à jour l'interface du panier quand des changements externes se produisent
   */
  onCartUpdate() {
    // Différentes mises à jour selon le type de panier (tiroir ou page)
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    const sectionId = isCartDrawer ? 'cart-drawer' : 'main-cart-items';

    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (isCartDrawer) {
          // Mise à jour du tiroir du panier
          this._updateCartDrawer(html);
        } else {
          // Mise à jour de la page du panier
          this._updateCartPage(html);
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la mise à jour du panier:', error);
      });
  }

  /**
   * Met à jour le tiroir du panier
   * @private
   */
  _updateCartDrawer(html) {
    const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
    selectors.forEach((selector) => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    });
  }

  /**
   * Met à jour la page du panier
   * @private
   */
  _updateCartPage(html) {
    const sourceQty = html.querySelector('cart-items');
    if (sourceQty) {
      this.innerHTML = sourceQty.innerHTML;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Met à jour la quantité d'un article dans le panier
   * @param {string|number} line - Identifiant de ligne de l'article
   * @param {number} quantity - Nouvelle quantité
   * @param {string} name - Nom de l'élément pour le focus
   * @param {string|number} variantId - ID de la variante
   */
  updateQuantity(line, quantity, name, variantId) {
    // Activer l'indicateur de chargement
    this.enableLoading(line);

    // Préparer et envoyer la requête
    const body = this._prepareQuantityUpdateBody(line, quantity);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this._handleQuantityUpdateSuccess(state, line, name, variantId))
      .catch((error) => this._handleQuantityUpdateError(error))
      .finally(() => this.disableLoading(line));
  }

  /**
   * Prépare le corps de la requête de mise à jour de quantité
   * @private
   */
  _prepareQuantityUpdateBody(line, quantity) {
    return JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
  }

  /**
   * Gère la réponse réussie de la mise à jour de quantité
   * @private
   */
  _handleQuantityUpdateSuccess(state, line, name, variantId) {
    const parsedState = JSON.parse(state);
    const quantityElement =
      document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    const items = document.querySelectorAll('.cart-item');

    // Gérer les erreurs de mise à jour
    if (parsedState.errors) {
      if (quantityElement) {
        quantityElement.value = quantityElement.getAttribute('value');
      }
      this.updateLiveRegions(line, parsedState.errors);
      return;
    }

    // Mettre à jour l'état vide/non-vide
    toggleCartEmptyState(this, parsedState.item_count === 0);

    // Mettre à jour le DOM
    this._updateCartSections(parsedState);

    // Vérifier si la quantité a été modifiée par le serveur
    this._checkQuantityChanges(parsedState, line, quantityElement, items);

    // Gérer le focus
    this._setFocusAfterQuantityUpdate(line, name, parsedState.item_count === 0);

    // Publier l'événement de mise à jour
    safePublishEvent('cartUpdate', {
      source: 'cart-items',
      cartData: parsedState,
      variantId: variantId,
    });
  }

  /**
   * Met à jour les sections du panier avec les données actualisées
   * @private
   */
  _updateCartSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });
  }

  /**
   * Vérifie si la quantité a été modifiée par le serveur et affiche un message si nécessaire
   * @private
   */
  _checkQuantityChanges(parsedState, line, quantityElement, items) {
    if (!quantityElement) return;

    const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
    let message = '';

    if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
      message =
        typeof updatedValue === 'undefined'
          ? window.cartStrings.error
          : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
    }

    this.updateLiveRegions(line, message);
  }

  /**
   * Définit le focus après une mise à jour de quantité
   * @private
   */
  _setFocusAfterQuantityUpdate(line, name, isEmpty) {
    const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
    if (!cartDrawerWrapper) return;

    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      if (typeof trapFocus === 'function') {
        cartDrawerWrapper
          ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
          : lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } else if (isEmpty && cartDrawerWrapper.querySelector('.drawer__inner-empty')) {
      if (typeof trapFocus === 'function') {
        trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
      }
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      if (typeof trapFocus === 'function') {
        trapFocus(cartDrawerWrapper, document.querySelector(SELECTORS.CART_ITEM_NAME));
      }
    }
  }

  /**
   * Gère les erreurs de mise à jour de quantité
   * @private
   */
  _handleQuantityUpdateError() {
    this.querySelectorAll(SELECTORS.LOADING_SPINNER).forEach((overlay) => overlay.classList.add('hidden'));
    handleCartErrors(null, window.cartStrings ? window.cartStrings.error : null);
  }
  /**
   * Met à jour les régions live pour l'accessibilité
   * @param {string|number} line - Identifiant de ligne de l'article
   * @param {string} message - Message à afficher
   */
  updateLiveRegions(line, message) {
    // Mettre à jour le message d'erreur spécifique à la ligne
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError && lineItemError.querySelector('.cart-item__error-text')) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
    }

    // Gérer l'élément de statut pour l'accessibilité
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    // Mettre à jour la région live globale
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  /**
   * Extrait le HTML intérieur d'une section
   */
  getSectionInnerHTML(html, selector) {
    return getSectionInnerHTMLSafe(html, selector);
  }

  /**
   * Active l'indicateur de chargement pour un article
   * @param {string|number} line - Identifiant de ligne de l'article
   */
  enableLoading(line) {
    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Vérifier si c'est un item dans un bundle (dans le details du bundle)
    const bundlePackItem = this.querySelector(`#CartItem-${line}.cart-item__pack-item`);

    if (bundlePackItem) {
      // Pour les items du bundle, afficher le spinner et masquer les contrôles
      const itemSpinner = bundlePackItem.querySelector('.cart-item__pack-item-loading');
      const removeButton = bundlePackItem.querySelector('cart-remove-button');
      const quantityWrapper = bundlePackItem.querySelector('.cart-item__pack-quantity-wrapper');

      if (itemSpinner) {
        itemSpinner.classList.remove('hidden');
      }
      if (removeButton) {
        removeButton.style.visibility = 'hidden';
      }
      if (quantityWrapper) {
        quantityWrapper.style.visibility = 'hidden';
      }
    } else {
      // Afficher les spinners de chargement normaux
      const cartItemElements = this.querySelectorAll(`#CartItem-${line} ${SELECTORS.LOADING_SPINNER}`);
      const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} ${SELECTORS.LOADING_SPINNER}`);

      [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.remove('hidden');
      });
    }

    // Enlever le focus actif
    document.activeElement.blur();

    // Mettre à jour le statut pour l'accessibilité
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Désactive l'indicateur de chargement pour un article
   * @param {string|number} line - Identifiant de ligne de l'article
   */
  disableLoading(line) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    // Vérifier si c'est un item dans un bundle (dans le details du bundle)
    const bundlePackItem = this.querySelector(`#CartItem-${line}.cart-item__pack-item`);

    if (bundlePackItem) {
      // Pour les items du bundle, masquer le spinner et restaurer les contrôles
      const itemSpinner = bundlePackItem.querySelector('.cart-item__pack-item-loading');
      const removeButton = bundlePackItem.querySelector('cart-remove-button');
      const quantityWrapper = bundlePackItem.querySelector('.cart-item__pack-quantity-wrapper');

      if (itemSpinner) {
        itemSpinner.classList.add('hidden');
      }
      if (removeButton) {
        removeButton.style.visibility = 'visible';
      }
      if (quantityWrapper) {
        quantityWrapper.style.visibility = 'visible';
      }
    } else {
      // Masquer les spinners de chargement normaux
      const cartItemElements = this.querySelectorAll(`#CartItem-${line} ${SELECTORS.LOADING_SPINNER}`);
      const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} ${SELECTORS.LOADING_SPINNER}`);

      [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.add('hidden');
      });
    }
  }
}
// Enregistrer le composant personnalisé CartItems
customElements.define('cart-items', CartItems);

/**
 * Composant CartNote - Gère les notes de panier
 * Ce composant permet à l'utilisateur d'ajouter une note à sa commande
 * et l'envoie automatiquement au serveur après un délai
 */
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        // Écouter les modifications de la note et les envoyer au serveur
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
